import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import '../../assets/scss/setPassword.scss';
import PasswordStrengthHints from '../../components/PasswordStrengthHints';
import { buildApiUrl } from '../../utils/apiUrl';
import { adaAlert } from '../../utils/adaSwal';
import {
	MAX_LENGTH,
	validatePassword,
} from '../../utils/passwordPolicy';

const SetPasswordForm = () => {
	const [searchParams] = useSearchParams();
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const token = searchParams.get('token');
	const [passwordSet, setPasswordSet] = useState(false);
	const [hints, setHints] = useState({ username: '', email: '' });
	const [hintsStatus, setHintsStatus] = useState('loading');

	useEffect(() => {
		if (!token) {
			setHintsStatus('error');
			return;
		}

		let cancelled = false;
		const load = async () => {
			try {
				const response = await fetch(buildApiUrl('auth/set-password/hints'), {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ token }),
				});
				const data = await response.json().catch(() => ({}));
				if (cancelled) {
					return;
				}
				if (!response.ok) {
					setHintsStatus('error');
					return;
				}
				setHints({
					username: data.username || '',
					email: data.email || '',
				});
				setHintsStatus('ok');
			} catch {
				if (!cancelled) {
					setHintsStatus('error');
				}
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [token]);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!token) {
			await adaAlert('Ugyldig lenke. Be om ny lenke.', 'Feil');
			return;
		}
		if (password !== confirmPassword) {
			await adaAlert('Passordene er ikke like. Prøv igjen.', 'Feil');
			return;
		}

		const policy = validatePassword(password, {
			username: hints.username,
			email: hints.email,
		});
		if (!policy.ok) {
			await adaAlert(policy.errors[0], 'Passordkrav');
			return;
		}

		try {
			const response = await fetch(buildApiUrl('auth/set-password'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ token, password }),
			});

			const data = await response.json();
			if (response.ok) {
				if (process.env.NODE_ENV === 'development') {
					console.log('[SetPasswordForm] passord er satt');
				}
				setPasswordSet(true);
			} else {
				await adaAlert(
					data.message ||
						'Noe gikk galt ved setting av passord. Prøv igjen senere.',
					'Feil'
				);
			}
		} catch (error) {
			console.error('Error setting password:', error);
		}
	};

	return (
		<div className='setPasswordFormContainer'>
			<div className='setPasswordForm'>
				<h2>Sett ditt passord</h2>
				{hintsStatus === 'error' && token ? (
					<p className='setPasswordForm__hintmsg'>
						Kunne ikke hente brukerdata for sjekk. Du kan fortsatt prøve å
						sette passord — ugyldig passord avvises ved innsending.
					</p>
				) : null}
				<form onSubmit={handleSubmit}>
					<div>
						<label htmlFor='password'>Passord</label>
						<input
							type='password'
							id='password'
							autoComplete='new-password'
							maxLength={MAX_LENGTH}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
						/>
						<PasswordStrengthHints
							password={password}
							username={hints.username}
							email={hints.email}
						/>
					</div>
					<div>
						<label htmlFor='confirmPassword'>Bekreft passord</label>
						<input
							type='password'
							id='confirmPassword'
							autoComplete='new-password'
							maxLength={MAX_LENGTH}
							value={confirmPassword}
							onChange={(e) => setConfirmPassword(e.target.value)}
						/>
					</div>
					<input type='submit' value='Sett passord' />
				</form>
				{passwordSet && (
					<>
						<p>Passordet ditt er nå endret!</p> <br />{' '}
						<a href='/login'>Til innlogging</a>
					</>
				)}
			</div>
		</div>
	);
};

export default SetPasswordForm;
